// Score movement between two scans.
//
// The reveal modal displays the score on a /10 scale (78 → 7.8), so the delta is
// expressed on that same scale — "+0.4" next to a 7.8 reads correctly, "+4"
// would not.
//
// HONESTY MATTERS HERE. The reveal chip was built green with an up-arrow and
// nothing else, so a decline rendered as green progress. This returns an
// explicit direction and the modal styles from it. The brand is "positive
// looksmaxxing", but positive means encouraging, not misreporting — a user who
// sees a fake improvement once will not trust the score again, and the score is
// the entire product.
//
// Movement under 0.05 on the /10 scale is reported as flat rather than as a
// rounded ±0.1: the model is not precise enough for that to mean anything, and
// manufacturing tiny wins is the same lie in miniature.

const FLAT_EPSILON = 0.05;

export const DELTA_UP = 'up';
export const DELTA_DOWN = 'down';
export const DELTA_FLAT = 'flat';

/**
 * @param {number} current   overall_score, 1-100
 * @param {number} previous  overall_score of the prior scan, 1-100
 * @returns {{value: number, direction: string, label: string} | null}
 *          null when there is no comparable prior scan (first-ever scan).
 */
export function computeScoreDelta(current, previous) {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;

  const value = (current - previous) / 10;

  if (Math.abs(value) < FLAT_EPSILON) {
    return { value: 0, direction: DELTA_FLAT, label: 'Holding steady' };
  }

  const magnitude = Math.abs(value).toFixed(1);
  if (value > 0) {
    return { value, direction: DELTA_UP, label: `+${magnitude} since last scan` };
  }
  // Minus sign (U+2212), not a hyphen — it aligns with the digits.
  return { value, direction: DELTA_DOWN, label: `−${magnitude} since last scan` };
}

// Whole days between two ISO timestamps, floored. Used for "6 days since your
// last reading" — returns null for anything unparseable rather than NaN.
export function daysSince(iso, now = Date.now()) {
  if (typeof iso !== 'string' || !iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / 86400000));
}
